import { DocumentParameters } from 'api3';
import { Component, Input, ViewEncapsulation, effect, inject, signal } from '@angular/core';
import { Api3Service } from '@app/services/api3.service';
import { VWData } from '@app/article/renderer/vw/volkswagen.types';
import { LoadingDotsComponent } from '@app/chat/components/loading-dots/loading-dots.component';
import { VWRendererComponent } from '../renderer/vw/vw-renderer.component';
import { TranslocoPipe } from '@jsverse/transloco';
import * as Sentry from '@sentry/angular';

@Component({
  selector: 'app-vw-articles',
  standalone: true,
  imports: [TranslocoPipe, LoadingDotsComponent, VWRendererComponent],
  template: `
    @if (isLoading()) {
      <app-loading-dots />
    } @else if (detailsData()) {
      <app-vw-renderer
        [rawData]="detailsData()"
        [provider]="documentParameters!.provider || 'volkswagen'"
        [tecDocPrototypes]="documentParameters?.tecDocPrototypes?.split(',') || []" />
    } @else if (isLegacyVwCitation()) {
      <div class="mt-8 text-center text-sm text-gray-500 dark:text-gray-400">{{ 'diagnosis.legacyVwArticle' | transloco }}</div>
    } @else {
      <div class="mt-8 text-center text-sm text-gray-500 dark:text-gray-400">{{ 'diagnosis.noDataAvailable' | transloco }}</div>
    }
  `,
  encapsulation: ViewEncapsulation.None,
})
export class VwArticlesComponent {
  @Input({ required: true }) documentParameters?: DocumentParameters & { provider?: string; tecDocPrototypes?: string };

  api3Service = inject(Api3Service);

  detailsData = signal<object | undefined>(undefined);
  isLoading = signal(true);
  isLegacyVwCitation = signal(false);

  init = effect(async () => {
    if (!this.documentParameters) {
      console.warn('[VwArticlesComponent] documentParameters is undefined or missing.');
      this.isLoading.set(false);
      return;
    }

    const documents = await this._loadArticles(this.documentParameters);

    this.isLoading.set(false);
    this.detailsData.set(documents);
  });

  private async _loadArticles(documentParameters: DocumentParameters & { provider?: string }): Promise<VWData | undefined> {
    let articleEntry: any;
    let error: any;

    if (documentParameters.provider === 'volkswagen') {
      const useElasticsearchId = !documentParameters.wdGroupNumber && !!documentParameters.elasticsearchId;

      if (!useElasticsearchId && !documentParameters.wdGroupNumber) {
        this.isLegacyVwCitation.set(true);
        return;
      }

      const result = await (useElasticsearchId
        ? this.api3Service.api.rmi.getVwByElasticsearchId({
            elasticsearchId: documentParameters.elasticsearchId!,
          })
        : this.api3Service.api.rmi.getVwById({
            wdGroupNumber: documentParameters.wdGroupNumber!,
          }));

      articleEntry = result.data;
      error = result.error;
    } else if (documentParameters.provider === 'skoda') {
      const useElasticsearchId =
        !documentParameters.mainChapterId && !documentParameters.chapterId && !documentParameters.wdGroupNumber && documentParameters.elasticsearchId;

      const result = await (useElasticsearchId
        ? this.api3Service.api.rmi.getSkodaByElasticsearchId({
            elasticsearchId: documentParameters.elasticsearchId!,
          })
        : this.api3Service.api.rmi.getSkodaById({
            repairGroupNumber: documentParameters.repairGroupNumber!,
            mainChapterId: documentParameters.mainChapterId!,
            chapterId: documentParameters.chapterId!,
            xmlDocumentId: documentParameters.xmlDocumentId!,
            wdGroupNumber: documentParameters.wdGroupNumber,
          }));

      articleEntry = result.data;
      error = result.error;
    } else if (documentParameters.provider === 'seat') {
      const useElasticsearchId =
        !documentParameters.mainChapterId && !documentParameters.chapterId && !documentParameters.wdGroupNumber && documentParameters.elasticsearchId;

      const result = await (useElasticsearchId
        ? this.api3Service.api.rmi.getSeatByElasticsearchId({
            elasticsearchId: documentParameters.elasticsearchId!,
          })
        : this.api3Service.api.rmi.getSeatById({
            repairGroupNumber: documentParameters.repairGroupNumber!,
            mainChapterId: documentParameters.mainChapterId!,
            chapterId: documentParameters.chapterId!,
            xmlDocumentId: documentParameters.xmlDocumentId!,
            wdGroupNumber: documentParameters.wdGroupNumber,
          }));

      articleEntry = result.data;
      error = result.error;
    } else if (documentParameters.provider === 'audi') {
      const useElasticsearchId =
        !documentParameters.mainChapterId && !documentParameters.chapterId && !documentParameters.wdGroupNumber && documentParameters.elasticsearchId;

      const result = await (useElasticsearchId
        ? this.api3Service.api.rmi.getAudiByElasticsearchId({
            elasticsearchId: documentParameters.elasticsearchId!,
          })
        : this.api3Service.api.rmi.getAudiById({
            repairGroupNumber: documentParameters.repairGroupNumber!,
            mainChapterId: documentParameters.mainChapterId!,
            chapterId: documentParameters.chapterId!,
            xmlDocumentId: documentParameters.xmlDocumentId!,
            wdGroupNumber: documentParameters.wdGroupNumber,
          }));

      articleEntry = result.data;
      error = result.error;
    }

    if (error) {
      const sentryError = new Error(`Failed to load article data: ${error.message || 'Unknown error'}`);
      Sentry.captureException(sentryError, {
        tags: {
          location: 'VwArticlesComponent',
          feature: 'VW-ArticleView',
        },
        extra: {
          httpError: error,
        },
      });

      return;
    }

    try {
      return JSON.parse(articleEntry.chapter_json) as VWData;
    } catch (e) {
      Sentry.captureException(e, {
        tags: {
          location: 'VwArticlesComponent',
          feature: 'VW-ArticleView',
        },
        extra: {
          message: 'Failed to parse article data (chapter_json).',
          chapterJsonPreview: articleEntry.chapter_json?.substring(0, 200),
        },
      });

      return;
    }
  }
}
